import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Pencil, Check, X, RefreshCw, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import RichTextEditor from '@/components/ui/rich-text-editor';
import RichTextDisplay from '@/components/ui/rich-text-display';

interface EditableAISummaryProps {
  candidateId: string;
  skillsSummary: string | null;
  productsSummary: string | null;
  onUpdate: () => void;
}

const EditableAISummary = ({ candidateId, skillsSummary, productsSummary, onUpdate }: EditableAISummaryProps) => {
  const [editingSkills, setEditingSkills] = useState(false);
  const [editingProducts, setEditingProducts] = useState(false);
  const [skillsValue, setSkillsValue] = useState(skillsSummary || '');
  const [productsValue, setProductsValue] = useState(productsSummary || '');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { toast } = useToast();

  const handleSave = async (field: 'skills' | 'products') => {
    setSaving(true);
    try {
      const updateData = field === 'skills' 
        ? { ai_skills_summary: skillsValue }
        : { ai_products_summary: productsValue };

      const { error } = await supabase
        .from('candidates')
        .update(updateData)
        .eq('id', candidateId);

      if (error) throw error;

      toast({ title: 'Summary updated successfully' });
      if (field === 'skills') setEditingSkills(false);
      else setEditingProducts(false);
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error saving summary', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (field: 'skills' | 'products') => {
    if (field === 'skills') {
      setSkillsValue(skillsSummary || '');
      setEditingSkills(false);
    } else {
      setProductsValue(productsSummary || '');
      setEditingProducts(false);
    }
  };

  const regenerateSummary = async () => {
    setRegenerating(true);
    try {
      const { error } = await supabase.functions.invoke('generate-skills-summary', {
        body: { candidateId }
      });

      if (error) throw error;

      toast({ title: 'AI Summary regenerated successfully' });
      onUpdate();
    } catch (error: any) {
      toast({ title: 'Error regenerating summary', description: error.message, variant: 'destructive' });
    } finally {
      setRegenerating(false);
    }
  };

  if (!skillsSummary && !productsSummary) {
    return (
      <Card className="p-4 bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">AI Summary</h3>
          </div>
          <Button variant="outline" size="sm" onClick={regenerateSummary} disabled={regenerating}>
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Generate
          </Button>
        </div>
        <p className="text-sm text-muted-foreground text-center py-4">
          No AI summary available. Click Generate to create one.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-primary/5 border-primary/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">AI Summary</h3>
        </div>
        <Button variant="outline" size="sm" onClick={regenerateSummary} disabled={regenerating}>
          {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Regenerate
        </Button>
      </div>

      {/* Skills Summary */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-medium">Skills Overview</h4>
          {!editingSkills && skillsSummary && (
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setEditingSkills(true)}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
        {editingSkills ? (
          <div className="space-y-2">
            <RichTextEditor
              value={skillsValue}
              onChange={setSkillsValue}
              minHeight="100px"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleCancel('skills')} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={() => handleSave('skills')} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        ) : (
          skillsSummary ? (
            <RichTextDisplay content={skillsSummary} />
          ) : (
            <p className="text-sm text-muted-foreground italic">No skills summary available</p>
          )
        )}
      </div>

      {/* Products Summary */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-medium">Product Experience</h4>
          {!editingProducts && productsSummary && (
            <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => setEditingProducts(true)}>
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
        {editingProducts ? (
          <div className="space-y-2">
            <RichTextEditor
              value={productsValue}
              onChange={setProductsValue}
              minHeight="100px"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => handleCancel('products')} disabled={saving}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={() => handleSave('products')} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        ) : (
          productsSummary ? (
            <RichTextDisplay content={productsSummary} />
          ) : (
            <p className="text-sm text-muted-foreground italic">No product experience summary available</p>
          )
        )}
      </div>
    </Card>
  );
};

export default EditableAISummary;
